import { Link, NavLink } from 'react-router-dom';

export interface TabNavItem {
  readonly to: string;
  readonly label: string;
  /** A small count shown beside the label, like the numbers on a repository's tabs. */
  readonly count?: number;
  /** Match the route exactly, for a section whose URL is a prefix of the others. */
  readonly end?: boolean;
  /**
   * Says which tab is open when the route cannot: sections that differ only by query string
   * (`?tab=timeline`) all match the same path, so the caller decides.
   */
  readonly current?: boolean;
}

export interface TabNavProps {
  readonly label: string;
  readonly items: readonly TabNavItem[];
}

function Content({ item }: { readonly item: TabNavItem }): React.JSX.Element {
  return (
    <>
      <span>{item.label}</span>
      {item.count === undefined ? null : (
        <span className="tab-nav__count" aria-hidden="true">
          {String(item.count)}
        </span>
      )}
    </>
  );
}

/**
 * Compact section tabs for a record whose sections are real links: plain text, a small count,
 * and an underline on the open one. A project and the model inside it use the same component,
 * so they read as one navigation.
 */
export function TabNav({ label, items }: TabNavProps): React.JSX.Element {
  return (
    <nav aria-label={label} className="tab-nav">
      {items.map((item) =>
        item.current === undefined ? (
          <NavLink key={item.to} to={item.to} end={item.end} className="tab-nav__item">
            <Content item={item} />
          </NavLink>
        ) : (
          <Link
            key={item.to}
            to={item.to}
            className="tab-nav__item"
            aria-current={item.current ? 'page' : undefined}
          >
            <Content item={item} />
          </Link>
        )
      )}
    </nav>
  );
}
